import { useCallback, useEffect, useRef, useState } from 'react';

export const LOAD_TIMEOUT_MS = 15000;

/**
 * Shared async page-load state: loading → success | error (2026-07 audit,
 * step 5 — no spinner may spin forever, every failure gets a visible,
 * retryable error).
 *
 * - `loadFn` is the page's existing async fetch. Rejections become the error
 *   state (console.error preserved for diagnostics).
 * - A load still pending after `timeoutMs` (default 15s) is converted into
 *   the error state. Firestore promises can wait on connectivity forever —
 *   this is the backstop. If the slow load eventually succeeds, the error
 *   clears itself and the data appears.
 * - `retry` re-runs the load (wired to the Retry button in LoadErrorNotice).
 * - Dep changes and unmount invalidate in-flight runs (no setState-after-
 *   unmount, no stale run clobbering a newer one).
 *
 * Returns { loading, error, retry }.
 */
export function usePageLoad(loadFn, deps, { timeoutMs = LOAD_TIMEOUT_MS, enabled = true } = {}) {
  const [loading, setLoading] = useState(!!enabled);
  const [error, setError] = useState(null);
  const runRef = useRef(0);
  const loadRef = useRef(loadFn);
  loadRef.current = loadFn;

  const run = useCallback(async () => {
    const runId = ++runRef.current;
    setLoading(true);
    setError(null);
    const timer = setTimeout(() => {
      if (runRef.current === runId) {
        console.error(`Page load still pending after ${timeoutMs}ms — showing error state.`);
        setLoading(false);
        const timeoutError = new Error('Load timed out');
        timeoutError.code = 'timeout';
        setError(timeoutError);
      }
    }, timeoutMs);
    try {
      await loadRef.current();
      if (runRef.current === runId) {
        setLoading(false);
        setError(null); // a late success after the timeout self-heals
      }
    } catch (err) {
      console.error('Page load failed:', err);
      if (runRef.current === runId) {
        setLoading(false);
        setError(err);
      }
    } finally {
      clearTimeout(timer);
    }
  }, [timeoutMs]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    run();
    return () => {
      runRef.current += 1; // invalidate the in-flight run
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, run, ...deps]);

  return { loading, error, retry: run };
}

/**
 * The same 15s "no spinner spins forever" backstop, for screens whose data
 * arrives via an onSnapshot listener rather than a promise (2026-07 perf
 * audit, step 8 — the admin tabs). usePageLoad can't wrap those: a listener
 * has no single promise to await, and retry means re-subscribing.
 *
 * Pass the tab's own `loading` flag. While it stays true past `timeoutMs`,
 * this returns a timeout Error suitable for <LoadErrorNotice error={…}>; it
 * clears itself the moment the first snapshot lands.
 *
 * Pair the Retry button with a nonce in BOTH the listener effect's deps and
 * this hook's `resetKey`, so retrying re-subscribes AND restarts the clock —
 * `loading` never goes false on a hung listener, so the nonce is the only
 * thing that can tell us a fresh attempt began:
 *
 *   const [nonce, setNonce] = useState(0);
 *   const timeoutError = useLoadTimeout(loading, { resetKey: nonce });
 *   useEffect(() => { … onSnapshot … }, [nonce]);
 *   <LoadErrorNotice error={listenError || timeoutError} onRetry={() => setNonce(n => n + 1)} />
 */
export function useLoadTimeout(loading, { timeoutMs = LOAD_TIMEOUT_MS, resetKey = 0 } = {}) {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    setTimedOut(false); // every fresh attempt starts clean
    if (!loading) return undefined;
    const timer = setTimeout(() => {
      console.error(`Listener still pending after ${timeoutMs}ms — showing error state.`);
      setTimedOut(true);
    }, timeoutMs);
    return () => clearTimeout(timer);
  }, [loading, timeoutMs, resetKey]);

  if (!timedOut) return null;
  const err = new Error('Load timed out');
  err.code = 'timeout';
  return err;
}
